import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gift, Loader2, Sparkles, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

/**
 * DailySpinner — v3.99d.405
 *
 * Once-per-24h reward wheel for authenticated collectors. Renders
 * nothing for logged-out visitors (they see the signup banner
 * instead).
 *
 * Flow:
 *   1. GET /spinner/status → server tells us if the user is eligible
 *      AND what slices are on the wheel (in draw order).
 *   2. On click, POST /spinner/spin → server picks a slice, grants
 *      the reward server-side, returns the winning index.
 *   3. We spin the SVG wheel with a CSS transform for 3.4s, landing
 *      such that the winning slice sits under the top marker.
 *   4. A modal reveals the prize. Wheel then locks with a live
 *      countdown to the next eligible spin.
 *
 * Design choices:
 *   - SVG wheel (no image assets) so it stays crisp on every DPR
 *     and themes with the site's CSS variables.
 *   - Rotation is 360*6 + landing offset → dramatic multi-rev spin.
 *   - `data-testid` on the CTA button + on each slice for QA.
 */

const SLICE_COLORS = [
  "#0A3D2A",  // brand green
  "#D4AF37",  // gold
  "#0A3D2A",
  "#DB785A",  // brand orange
  "#D4AF37",
  "#0A3D2A",
  "#DB785A",
  "#D4AF37",
];

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function slicePath(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

function shortLabel(slice) {
  if (slice.kind === "funds") return `$${Math.round(slice.amount || 0)}`;
  if (slice.kind === "free_shipping") return "FREE\nSHIP";
  if (slice.kind === "product") return "FREE\nPACK";
  // v3.99d.410 — "Try Again" slice now reflects whatever the admin
  // typed as the slice label. We wrap it to fit inside the wedge:
  // split at whitespace into up to 2 lines (~7 chars each) and
  // upper-case the result to match the visual language of the other
  // slices. Falls back to a sensible default if admin left it blank.
  const raw = (slice.label || "Try again").toUpperCase();
  const words = raw.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "TRY";
  if (words.length === 1) {
    const w = words[0];
    if (w.length <= 7) return w;
    // Break long single word roughly in half at a nice point.
    const mid = Math.ceil(w.length / 2);
    return `${w.slice(0, mid)}\n${w.slice(mid)}`;
  }
  // 2+ words: greedy pack line 1 up to ~7 chars, rest on line 2.
  let l1 = words[0];
  let i = 1;
  while (i < words.length && (l1 + " " + words[i]).length <= 7) {
    l1 += " " + words[i];
    i += 1;
  }
  const l2 = words.slice(i).join(" ");
  return l2 ? `${l1}\n${l2}` : l1;
}

function Countdown({ isoAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!isoAt) return null;
  const diff = new Date(isoAt).getTime() - now;
  if (diff <= 0) return <span>ready now</span>;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return <span className="font-mono">{h}h {m}m {s}s</span>;
}

export default function DailySpinner() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [reveal, setReveal] = useState(null);
  const rotationRef = useRef(0);

  useEffect(() => {
    if (!user) return;
    api.get("/spinner/status")
       .then((r) => setStatus(r.data))
       .catch(() => setStatus(null));
  }, [user]);

  if (!user || !status) return null;
  // v3.99d.406 — Server-side master switch. When admin flips
  // `daily_spinner_enabled: false`, status returns `enabled: false`
  // and we render nothing so the widget disappears entirely from
  // the home page without any client-side flag needed.
  if (status.enabled === false) return null;

  const slices = status.slices || [];
  const n = slices.length;
  const slice = 360 / n;

  const spin = async () => {
    if (!status.eligible || spinning) return;
    setSpinning(true);
    try {
      const r = await api.post("/spinner/spin");
      const idx = r.data.slice_index;
      // Land such that the winning slice's CENTER sits under the top
      // pointer (12 o'clock). Slice[i] occupies [i*slice, (i+1)*slice)
      // measured clockwise from 0deg (which is where we start
      // drawing = top). Center offset = i*slice + slice/2.
      // We rotate the WHEEL negative that amount to bring the center
      // to top. Add 6 full revolutions for drama.
      const target = 360 * 6 - (idx * slice + slice / 2);
      // Ensure we always spin forward from wherever we last stopped.
      const from = rotationRef.current % 360;
      const next = target + (360 - from);
      rotationRef.current += next;
      setRotation(rotationRef.current);
      // Reveal after the CSS transition ends.
      setTimeout(() => {
        setReveal(r.data.reward);
        setStatus((s) => ({
          ...s,
          eligible: false,
          eligible_at: r.data.eligible_at,
          last_reward: r.data.reward,
        }));
        setSpinning(false);
      }, 3500);
    } catch (e) {
      toast.error(formatApiError(e));
      setSpinning(false);
    }
  };

  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;

  return (
    <div className="border p-5 flex items-center gap-5 flex-wrap sm:flex-nowrap"
         style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.06) 0%, rgba(10,61,42,0.10) 100%)",
                  borderColor: "var(--border)",
                  borderRadius: 2 }}
         data-testid="daily-spinner">
      {/* Wheel */}
      <div className="relative flex-shrink-0 mx-auto"
           style={{ width: size, height: size }}>
        {/* Pointer */}
        <div className="absolute left-1/2 -translate-x-1/2 z-10"
             style={{ top: -2 }}>
          <div style={{
            width: 0, height: 0,
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderTop: "16px solid var(--gold)",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))",
          }}/>
        </div>
        <motion.svg
          width={size} height={size} viewBox={`0 0 ${size} ${size}`}
          animate={{ rotate: rotation }}
          transition={{ duration: 3.4, ease: [0.15, 0.85, 0.25, 1] }}
          style={{ transformOrigin: "50% 50%" }}>
          {slices.map((s, i) => {
            const startA = i * slice;
            const endA = (i + 1) * slice;
            const midA = startA + slice / 2;
            const labelPos = polarToCartesian(cx, cy, r * 0.66, midA);
            const isNothing = s.kind === "nothing";
            const color = isNothing
              ? "rgba(23,26,31,0.9)"
              : SLICE_COLORS[i % SLICE_COLORS.length];
            const textColor = color === "#D4AF37" ? "#08090B" : "#F5F1E8";
            return (
              <g key={i} data-testid={`spinner-slice-${i}`}>
                <path d={slicePath(cx, cy, r, startA, endA)}
                      fill={color}
                      stroke="rgba(0,0,0,0.5)"
                      strokeWidth="1.5"/>
                <text x={labelPos.x} y={labelPos.y}
                      fill={textColor}
                      fontSize="12" fontWeight="700"
                      textAnchor="middle" dominantBaseline="middle"
                      transform={`rotate(${midA} ${labelPos.x} ${labelPos.y})`}
                      style={{ fontFamily: "'JetBrains Mono', monospace",
                               letterSpacing: "0.02em",
                               whiteSpace: "pre" }}>
                  {shortLabel(s).split("\n").map((line, li) => (
                    <tspan key={li} x={labelPos.x}
                           dy={li === 0 ? "-0.4em" : "1.1em"}>
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          })}
          {/* Hub */}
          <circle cx={cx} cy={cy} r="18"
                  fill="var(--gold)" stroke="#08090B" strokeWidth="2"/>
        </motion.svg>
      </div>

      {/* Copy + CTA */}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1 inline-flex items-center gap-1.5"
             style={{ color: "var(--gold)" }}>
          <Sparkles className="w-3 h-3"/> Daily Spin
        </div>
        <div className="font-serif tracking-tighter leading-tight text-2xl sm:text-3xl"
             style={{ color: "var(--text)" }}>
          Spin once a day. Win real rewards.
        </div>
        <div className="text-[13px] mt-2 max-w-md"
             style={{ color: "var(--text-2)" }}>
          Forge Funds, free shipping, and other collector perks — a
          fresh spin every 24 hours, on us.
        </div>
        {status.eligible ? (
          <button onClick={spin}
                  disabled={spinning}
                  data-testid="daily-spinner-cta"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold uppercase tracking-wider disabled:opacity-50 hover:brightness-110 transition"
                  style={{ background: "var(--gold)",
                            color: "#08090B",
                            borderRadius: 2,
                            boxShadow: "0 0 0 1px rgba(212,175,55,0.6), 0 8px 24px -6px rgba(212,175,55,0.5)" }}>
            {spinning
              ? <><Loader2 className="w-4 h-4 animate-spin"/> Spinning…</>
              : <><Gift className="w-4 h-4"/> Spin the wheel</>}
          </button>
        ) : (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-[11px] font-mono uppercase tracking-wider border"
               style={{ color: "var(--text-muted)",
                        borderColor: "var(--border)",
                        borderRadius: 2 }}
               data-testid="daily-spinner-locked">
            <Clock className="w-3 h-3"/>
            Next spin in <Countdown isoAt={status.eligible_at}/>
          </div>
        )}
      </div>

      {/* Reveal modal */}
      {reveal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.72)" }}
             onClick={() => setReveal(null)}
             data-testid="daily-spinner-reveal">
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="border p-8 max-w-sm text-center"
            style={{ background: "var(--surface)",
                     borderColor: "var(--gold)",
                     borderRadius: 2,
                     boxShadow: "0 0 0 1px rgba(212,175,55,0.4), 0 40px 80px -20px rgba(0,0,0,0.9)" }}>
            <div className="mx-auto w-14 h-14 flex items-center justify-center mb-4"
                 style={{ background: "var(--gold)",
                          color: "#08090B",
                          borderRadius: "50%" }}>
              {reveal.kind === "nothing"
                ? <Clock className="w-6 h-6"/>
                : <Gift className="w-6 h-6"/>}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                 style={{ color: "var(--gold)" }}>
              {reveal.kind === "nothing" ? "Not this time" : "You won!"}
            </div>
            <div className="font-serif tracking-tighter text-2xl mb-2"
                 style={{ color: "var(--text)" }}>
              {reveal.label}
            </div>
            {reveal.kind === "funds" && (
              <div className="text-sm"
                   style={{ color: "var(--text-2)" }}>
                Credited to your Forge Funds balance instantly.
              </div>
            )}
            {reveal.kind === "product" && reveal.product && (
              <>
                <div className="mt-3 mb-2 border p-2 flex items-center gap-3"
                     style={{ borderColor: "var(--border)",
                              background: "var(--surface-2)",
                              borderRadius: 2 }}>
                  {reveal.product.image && (
                    <img src={reveal.product.image} alt=""
                         className="w-14 h-14 object-cover flex-shrink-0"/>
                  )}
                  <div className="min-w-0 text-left">
                    <div className="text-xs truncate"
                         style={{ color: "var(--text)" }}>
                      {reveal.product.title}
                    </div>
                    <div className="text-[10px] font-mono line-through opacity-60">
                      ${Number(reveal.product.price || 0).toFixed(2)}
                    </div>
                    <div className="text-[10px] font-mono"
                         style={{ color: "var(--gold)" }}>
                      Your price: <strong>
                        {Number(reveal.product_price ?? 0) === 0
                          ? "FREE"
                          : `$${Number(reveal.product_price).toFixed(2)}`}
                      </strong>
                      {" · you cover shipping"}
                    </div>
                  </div>
                </div>
                <div className="text-sm"
                     style={{ color: "var(--text-2)" }}>
                  {reveal.added_to_cart
                    ? "Already in your cart. Check out to claim — you only cover shipping."
                    : "Head to your cart to claim this prize."}
                </div>
                <a href="/cart"
                   className="mt-4 inline-block px-4 py-2 text-[11px] font-mono uppercase tracking-wider"
                   style={{ background: "var(--gold)", color: "#08090B",
                            borderRadius: 2 }}
                   data-testid="daily-spinner-cart-link">
                  Open cart
                </a>
              </>
            )}
            {reveal.kind === "free_shipping" && (
              <div className="text-sm mt-3"
                   style={{ color: "var(--text-2)" }}>
                Applied automatically to your next order — no code
                needed. Just check out any time in the next 30 days.
              </div>
            )}
            {reveal.kind === "nothing" && (
              <div className="text-sm"
                   style={{ color: "var(--text-2)" }}>
                Come back tomorrow — the wheel resets in 24 hours.
              </div>
            )}
            <button onClick={() => setReveal(null)}
                    className="mt-6 w-full py-2.5 text-[11px] font-mono uppercase tracking-wider border"
                    style={{ borderColor: "var(--border)",
                             color: "var(--text-2)",
                             borderRadius: 2 }}
                    data-testid="daily-spinner-close">
              Close
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
